
import { EventEmitter } from "@angular/core";
import { Hotspot, HotspotType, VideoHotspot } from "@pcp/core/interfaces";
import { isNotNullOrUndefined } from "@pcp/core/util";
import { LoadedEvent, VimeoEvent } from "@vimeo/player";
import { catchError, filter, from, map, of, ReplaySubject, startWith, Subject, switchMap, takeUntil, tap, timeout } from "rxjs";
import { VimeoPlayer } from "./player-implementation/pcp-vimeo-player";
import { VideoDebugLogger } from "./video-debug-logger";
import { PlaybackPositionChangedEvent, VideoPausedEvent, VideoPlayerComponentEvent } from "./video-player-events";
import { VideoPlayerInputProps } from "./video-player-input";
import { VideoPlayerOptions } from "./video-player-options";
import { RangeValidator } from "./video-range-validator";
import { VideoPlayerConfigService } from "../../services/video-player-config.service";

export class ReactivePlayer {

    private player: VimeoPlayer;
    private options!: VideoPlayerOptions;
    private validator = new RangeValidator();

    /**
     * Time in seconds where the player stopped automatically because it has reached a pause point
     * or undefied.
     */
    private currentPausePoint?: number;

    // TODO: do not emit events while events are paused?
    private readonly videoStartedPlayingSubject = new EventEmitter<PlaybackPositionChangedEvent>();
    private readonly videoPausedSubject = new EventEmitter<VideoPausedEvent>();
    private readonly videoLoadedSubject = new EventEmitter<VideoPlayerComponentEvent>();
    private readonly seekedToPositionSubject = new EventEmitter<PlaybackPositionChangedEvent>();
    private readonly timeUpdateSubject = new EventEmitter<PlaybackPositionChangedEvent>();
    private readonly hotspotClickedSubject = new EventEmitter<VideoHotspot>();
    private readonly videoEndedSubject = new EventEmitter<void>();

    readonly videoStartedPlaying$ = this.videoStartedPlayingSubject.asObservable();
    readonly videoPaused$ = this.videoPausedSubject.asObservable();
    readonly videoLoaded$ = this.videoLoadedSubject.asObservable();
    readonly seekedToPosition$ = this.seekedToPositionSubject.asObservable().pipe(
        filter(event => this.validator.getIsInRange(event.seconds)),
    );
    readonly timeUpdate$ = this.timeUpdateSubject.asObservable();
    readonly hotspotClicked$ = this.hotspotClickedSubject.asObservable();
    readonly videoEnded$ = this.videoEndedSubject.asObservable();

    // Getters, not field initializers: `this.player` is only assigned in the
    // constructor, so a field initializer would read it before it exists.
    /** See `VimeoPlayer.autoplayBlocked$` — a video that could not start with sound. */
    get autoplayBlocked$() {
        return this.player.autoplayBlocked$;
    }
    /** See `VimeoPlayer.playbackError$`. */
    get playbackError$() {
        return this.player.playbackError$;
    }

    private readonly destroyed$ = new Subject<void>();

    private readonly round = Math.floor;

    private _video!: VideoPlayerInputProps;

    /**
     * Hotspots of type "E":
     * Component will emit an event once this hotspot's `FrameStart` is reached.
     * Hotspots of type "E" should automatically navigate to another element once reached.
     */
    private elementHotspots: VideoHotspot[] = [];

    private readonly videoSubject = new ReplaySubject<VideoPlayerInputProps>(1);
    private readonly video$ = this.videoSubject.asObservable();
    private readonly availableHotspots$ = this.video$.pipe(
        map(video => [...video.hotspots]),
    );
    readonly visibleHotspots$ = this.availableHotspots$.pipe(
        switchMap(hotspots => this.timeUpdate$.pipe(
            map(event => event.seconds),
            startWith(0), // if no timeupdate event has been triggered yet
            map(currentPosition => hotspots.filter(hotspot =>
                currentPosition !== undefined
                && Number.isFinite(currentPosition)
                && hotspot.frameStart !== undefined
                && hotspot.frameEnd !== undefined
                && hotspot.frameStart <= Math.round(currentPosition)    //"Math.round" is used to show hotspots at "this.video.range.end - 0.2" timestamp, where "this.video.range.end" is integer number
                && hotspot.frameEnd >= Math.floor(currentPosition)
            )),
        )),
    );

    private set video(v: VideoPlayerInputProps) {
        this.videoSubject.next(v);
        this._video = v;
        this.elementHotspots = v
            .hotspots
            .filter((hotspot: VideoHotspot) => hotspot.type === HotspotType.element);
    }

    private get video() {
        return this._video;
    }

    private readonly logger: VideoDebugLogger;

    constructor(
        readonly element: HTMLIFrameElement | HTMLElement | string,
        video: VideoPlayerInputProps,
        private readonly globalPlayerConfig: VideoPlayerConfigService,
    ) {
        this.logger = new VideoDebugLogger(this);
        this.player = new VimeoPlayer(element);
        this.video = video;

        // this.player.play$.pipe(
        //     takeUntil(this.destroyed$),
        //     concatMap(event => this.onPlayVideo(event))
        // ).subscribe();
        // this.player.pause$.pipe(
        //     takeUntil(this.destroyed$),
        //     tap(event => this.onPauseVideo(event))
        // ).subscribe();
        // this.player.seeked$.pipe(
        //     takeUntil(this.destroyed$),
        //     concatMap(event => this.onSeekedVideo(event))
        // ).subscribe();

        // this.player.timeupdate$.pipe(
        //     takeUntil(this.destroyed$),
        //     concatMap(event => this.onTimeupdate(event))
        // ).subscribe();
        // this.player.ended$.pipe(
        //     takeUntil(this.destroyed$),
        //     concatMap(async event => {
        //         // seek back back for a minimum amount:
        //         // --> prevents the video from finishing and showing an end screen
        //         await this.player.seekToNoEmit(event.seconds - 0.2);
        //         this.player.pauseNoEmit();
        //     }),
        //     tap(event => this.videoEndedSubject.emit()),
        // ).subscribe();

        this.player.onPlayerEvent$.pipe(
            takeUntil(this.destroyed$),
            tap(event => {
                if (event.type !== 'timeupdate') {
                    this.logger.debug('onPlayerEvent$ triggered', event);
                }
            }),
            // concatMap(async event => {
            switchMap(async event => {
                from(new Promise(async () => {
                    switch (event.type) {
                        case 'play':
                            return await this.onPlayVideo(event);
                        case 'pause':
                            return await this.onPauseVideo(event);
                        case 'seeked':
                            return await this.onSeekedVideo(event);
                        case 'timeupdate':
                            return await this.onTimeupdate(event);
                        case 'ended':

                            // If autoclose = true, let the video end normally
                            // -> important for video questions in order to close the video and return to the question element.
                            //
                            // If autoclose = false, seek back a little bit and pause the video

                            if (this.video.autoClose) {
                                this.videoEndedSubject.next();
                                return;
                            }

                            //fix wrong "event.seconds" value at the end of video range:
                            const endSeconds = this.validator.getSecondsOrRangeLimit(event.seconds);
                            // seek back back for a minimum amount:
                            // --> prevents the video from finishing and showing an end screen
                            await this.player.seekToNoEmit(endSeconds - 0.2);
                            return await this.player.pauseNoEmit();
                    }
                })).pipe(
                    timeout(1000),
                    catchError(error => {
                        this.logger.error('player event did not complete', event);
                        return of(undefined);
                    }),
                );
            })
        ).subscribe();

        this.player.loaded$.pipe(
            takeUntil(this.destroyed$),
            tap(event => this.onLoadedVideo(event))
        ).subscribe();
        // cache volume changes for the next time a video plays
        this.player.volumechange$.pipe(
            tap(event => {
                this.globalPlayerConfig.setVolume(event.volume);
            }),
            takeUntil(this.destroyed$),
        ).subscribe();
    }

    destroy = () => {
        this.destroyed$.next();
        this.destroyed$.complete();
        return this.player.destroy();
    };
    getBuffered = () => this.player.getBuffered();
    getCurrentTime = () => this.player.getCurrentTime();
    getDuration = () => this.player.getDuration();
    getEnded = () => this.player.getEnded();
    getMuted = () => this.player.getMuted();
    getPaused = () => this.player.getPaused();
    getSeekable = () => this.player.getSeekable();
    getSeeking = () => this.player.getSeeking();
    getVideoId = () => this.player.getVideoId();
    getVolume = () => this.player.getVolume();
    ready = async () => await this.player.ready();
    setCurrentTime = (targetPosition: number) => this.player.setCurrentTime(targetPosition);
    setMuted = async (isMuted: boolean) => {
        const volume = this.globalPlayerConfig.getVolume();

        if (this.player && !(volume && volume > 0)) {
            // Volume takes precedence over the muted setting:
            // If the volume is set to a number > 0, the player should _not_ be muted.
            await this.player.ready();
            await this.player.setMuted(isMuted);
        }
    };
    setVolume = (volume: number) => this.player.setVolume(volume);
    unload = () => this.player.unload();

    /**
     * First load.
     * this.video must be already defined.
     */
    private async createPlayer(options: VideoPlayerOptions) {
        const volume = this.globalPlayerConfig.getVolume();
        await this.player.init(options, volume);

        // Player must be ready before proceeding!
        await this.player.ready();

        // NOTE: as we're both emitting and receiving the volume to/from the config service,
        //       we create some kind of 2-way data binding.
        //       As setting the volume on the player is async, emissions and receiving
        //       may not be in sync. This can cause a loop of setting the volume between two values again and again.
        //       The debounce from the service should do the trick,
        //       but if we're still facing issues, we could for ex. just listen on `0` values.
        this.globalPlayerConfig.volume$
            .pipe(takeUntil(this.destroyed$))
            .subscribe(vol => {
                if (vol !== undefined) {
                    this.player.setVolume(vol);
                }
            });

        // (re-)emit the current position:
        // the last emitting client will emit in order to all other clients seek back
        // TODO: would be nicer if all emitters would wait for each other (ready + buffer end) and then play simultaneously.
        // TODO: instead of emitting seeked - emit loaded + current time?
        // TODO: check: is `isInPausedState` always `false`?
        // this.seekedToPositionSubject.next({ isInPausedState: false, player: this, seconds: await this.player.getCurrentTime() });
    }

    // actions:
    // external input telling the player to do something

    private isFirstRun = true;

    public async playVideo(video: VideoPlayerInputProps, options: VideoPlayerOptions) {
        this.video = video;
        this.options = options;
        this.validator = new RangeValidator(video.range ?? undefined);
        this.resetCurrentPausePoint();

        // initialize player on the first run
        if (this.isFirstRun) {
            this.isFirstRun = false;
            await this.createPlayer(options);
            return;
        }

        try {
            let startPaused = true;
            if (options.forceAutoplay === true) {
                // always start playing if forceAutoplay is true
                this.logger.log('[Video Debug]: ReactivePlayer: start playing because "forceAutoplay" is set to "true"');
                startPaused = false;
            } else {
                // Start any other video (new video ID) in paused state
                this.logger.log('[Video Debug]: ReactivePlayer: start paused because no other condition matched');
                startPaused = true;
            }

            if (
                this.player.rangeHasChanged(options)
                || this.player.videoIdHasChanged(options) && VimeoPlayer.rangeHasValue(options)
            )
            {
                //create new player instance when video range has changed. Otherwise seekbar contains timeframes outside of current range.
                await this.player.removeAllListenersAndDestroy();
                await this.createPlayer(options);
            } else {
                await this.player.loadNextVideo(options, startPaused);
            }

        } catch (error: any | Error) {
            await this.player.unload();
            // await this.player.destroy();
            this.logger.error(error);
        }
    }

    /**
     * Public interface, should not be used internally!
     * Start/resume playing the video.
     *
     * If `atPosition` is given, resumes the video at the given time.
     * If the given time is out of the video range,
     * the target position will be set to the closest range limit.
     */
    public async play(atPosition?: number): Promise<void> {
        await this.player.playNoEmit(atPosition);
    }

    public async pause() {
        await this.pausePlayer();
    }

    public async seekTo(seconds: number, paused?: boolean): Promise<void> {
        this.logger.debug('received "seekTo"', seconds);
        const targetPosition = this.validator.getSecondsOrRangeLimit(this.round(seconds));

        try {
            await this.player.seekToNoEmit(targetPosition, paused);
        } catch (err) {
            this.logger.error(err);
            const error = err as Error | RangeError;
            switch (error.name) {
                case 'RangeError':
                    this.logger.log('ERROR: The timecode is out of range: ' + targetPosition);
                    break;
                default:
                    this.logger.log('ERROR: While setting timecode: ' + error.message);
                    break;
            }
        } finally {
            this.logger.debug('processed "seekTo"');
        }
    }

    /**
     * Restart the video from the beginning (0 or range start).
     * Resets all hotspots and pause points.
     */
    async restart() {
        this.resetCurrentPausePoint();
        let videoStartPosition = 0;
        if (isNotNullOrUndefined(this.video.range) && this.video.range.start > 0) {
            videoStartPosition = this.video.range.start;
        }


        // NOTE: using `seekTo` + emit
        //       as the restart (content reset) won't be emitted as "show a new slide" for video slides.
        //       The emitting player should just send a SeekToCommand in order for the consumers
        //       to go to the same position.
        await this.player.seekTo(videoStartPosition);
    }

    resetCurrentPausePoint() {
        this.currentPausePoint = undefined;
    }


    // internal vimeo player events:
    // actions performed on the player. Output result using EventEmitters.

    private onLoadedVideo(data: LoadedEvent) {
        this.resetCurrentPausePoint();
        this.videoLoadedSubject.next({ player: this });
    }

    private async onPlayVideo(data: VimeoEvent) {
        this.logger.debug('internal player event: onPlayVideo', data, 'is seeking:', await this.player.getSeeking());
        if (this.currentPausePoint) {
            await this.setCurrentTime(this.currentPausePoint + 1);
            this.resetCurrentPausePoint();
            this.currentPausePoint = undefined;
        }

        // await this.zone.run(async () => {
        const seconds = await this.player.getCurrentTime();

        const isPaused = await this.player.getPausedWithTimeout();
        this.videoStartedPlayingSubject.next({ player: this, seconds, isInPausedState: isPaused });
        // });
    }

    private async onPauseVideo(event: VimeoEvent) {
        const isSeeking = await this.player.getSeekingWithTimeout();
        this.logger.debug('internal player event: onPauseVideo', event, 'is seeking:', isSeeking);

        // if percent === 1 ==> video has ended
        // ignore the event if autoClose === true
        if (this.video.autoClose === true && event.percent === 1) {
            return;
        }

        // await this.player.ready();
        if (!isSeeking) {
            this.videoPausedSubject.next({ ...event, player: this });
        }
    }

    private async onSeekedVideo(data: VimeoEvent) {
        this.logger.debug('internal player event: onSeekedVideo', data);

        await this.player.ready();
        this.resetCurrentPausePoint();

        const seconds = this.round(this.validator.getSecondsOrRangeLimit(data.seconds));
        const isInPausedState = await this.player.getPausedWithTimeout();

        if (typeof this.video.pausePoints === 'object' && this.video.pausePoints?.includes(seconds)) {
            this.seekedToPositionSubject.next({ player: this, seconds, isInPausedState });
            return; // do not emit the previous seek as it was out of bounds -> wait for the next seek to fire
        }

        this.seekedToPositionSubject.next({ player: this, seconds, isInPausedState });
        this.logger.debug('internal player event: onSeekedVideo finished', data);
    }

    /**
     * Triggered whenever the playback position changed:
     * https://developer.vimeo.com/player/sdk/reference#timeupdate
     */
    private async onTimeupdate(data: VimeoEvent) {
        const isInPausedState = await this.player.getPaused();
        this.timeUpdateSubject.next({ seconds: data.seconds, player: this, isInPausedState });

        const seconds = this.round(data.seconds);

        if (this.currentPausePoint === undefined && typeof this.video.pausePoints === 'object' && this.video.pausePoints?.includes(seconds)) {
            this.logger.warn('pausePlayer because video has reached a pause point', this.currentPausePoint, this.video.pausePoints);
            this.currentPausePoint = seconds;
            if (!await this.player.getPaused()) {
                await this.pausePlayer();
            }
        }

        const reachedElementHotspot = this.elementHotspots.filter(hotspot =>
            hotspot.frameStart !== undefined
            && hotspot.frameEnd !== undefined
            && seconds >= hotspot.frameStart
            // ignore hotspots which are not contained in the current video range
            && hotspot.frameStart >= (this.video.range?.start ?? 0)
        ).pop();
        if (reachedElementHotspot) {
            this.logger.warn('pausePlayer because element hotspot is reached', reachedElementHotspot);
            if (!await this.player.getPaused()) {
                await this.pausePlayer();
                // reaching a hotspot of the "element" has the same effect as clicking a visual hotspot manually
                this.activateHotspot(reachedElementHotspot);
            }
        }
    }

    activateHotspot(hotspot: Hotspot) {
        if (!this.video.hotspots.includes(hotspot)) {
            this.logger.error(this.video, hotspot);
            throw new Error(`Unable to activate hotspot: video does not include the specified hotspot`);
        }

        this.hotspotClickedSubject.next(hotspot);
    }

    /**
     * Pause the Vimeo Player without emitting an pause event.
     */
    async pausePlayer(): Promise<void> {
        this.logger.warn('pausePlayer without emit called from public interface');
        await this.player.pauseNoEmit();
    }
}
